import { DownArrowIcon } from "@xola/icons";
import { Button, Currency, Label, Popover } from "@xola/ui-kit";
import { getItemLineItemsByTypes, isEquipmentItem } from "common";
import { sumBy } from "lodash";
import pluralize from "pluralize";
import React, { useState } from "react";
import {
    Experience,
    ExperiencePurchaseItem,
    LineItem,
    LineItemCode,
    LineItemType,
    Purchase,
    PurchaseItem,
} from "types";
import { QuantitySpinner } from "../../Common/QuantitySpinner/QuantitySpinner";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { Loader } from "../../Loader";
import { SplitPayDemographics, SplitPayFieldValue, SplitPaymentInitialValues } from "./CreatePurchaseSplitPayTypes";
import { getDemographicsTotalSum } from "./helpers";
import { ExperienceTile, SplitPayWarningAlert } from "./SplitPayComponents";
import { Breakdown } from "@/components/Common/Breakdown";
import { useSeller } from "@/hooks/useAccount";
import { useResource } from "@/hooks/useResource";
import { getPartnerFee, isEquipmentPurchase } from "@/models/Purchase";
import { AddonLineItem, DemographicLineItem } from "@/stores/createPurchaseStore";
import { numberFormat } from "@/utils/numbers";

interface SplitPaySelectDemographicsProps {
    splitPayDemographics: SplitPayDemographics;
    purchase: Purchase;
    popoverParentId: string;
    onSplitPayDemographicsChange: (splitPayDemographics: SplitPayDemographics) => void;
    onSubmit: (amount: number) => void;
    onClose: () => void;
}

export const SplitPaySelectDemographics = ({
    purchase,
    splitPayDemographics,
    popoverParentId,
    onSplitPayDemographicsChange,
    onSubmit,
    onClose,
}: SplitPaySelectDemographicsProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const content = document.querySelector(`#${popoverParentId}`);

    const isEquipment = isEquipmentPurchase(purchase);
    let demographics = 0;
    let addons = 0;
    for (const { splitPayItemValues } of splitPayDemographics.items) {
        demographics += sumBy(splitPayItemValues.demographics, "quantity");
        addons += sumBy(splitPayItemValues.addons, "quantity");
    }

    const getDisplayText = () => {
        if (demographics > 0 || addons > 0) {
            const unitLabel = isEquipment
                ? pluralize("equipment unit", demographics)
                : pluralize("guest", demographics);
            return `${demographics} ${unitLabel} and ${pluralize("add-on", addons)}`;
        }
        return isEquipment ? "specific equipments and add-ons" : "specific guests and add-ons";
    };

    return (
        <Popover
            zIndex={50}
            visible={isVisible}
            maxWidth={350}
            minWidth={500}
            offset={[0, 10]}
            appendTo={content}
            placement="right"
            trigger="click"
            onClickOutside={() => {
                setIsVisible(false);
            }}
        >
            <span
                onClick={() => {
                    setIsVisible(true);
                }}
            >
                <span> </span>
                <strong className="underline">{getDisplayText()}</strong>
                <DownArrowIcon className="flex-shrink-0" size="medium" />
            </span>
            <Popover.Content>
                <SelectDemographicAndAddonPopoverContent
                    splitPayDemographics={splitPayDemographics}
                    purchase={purchase}
                    onSplitPayDemographicsChange={onSplitPayDemographicsChange}
                    onSubmit={(amount: number) => {
                        setIsVisible(false);
                        onSubmit(amount);
                    }}
                    onCancel={() => {
                        setIsVisible(false);
                        onClose();
                    }}
                />
            </Popover.Content>
        </Popover>
    );
};

interface SelectDemographicAndAddonPopoverContentProps {
    splitPayDemographics: SplitPayDemographics;
    onSplitPayDemographicsChange: (values: SplitPayDemographics) => void;
    purchase: Purchase;
    onCancel: () => void;
    onSubmit: (amount: number) => void;
}

const SelectDemographicAndAddonPopoverContent = ({
    splitPayDemographics,
    onSplitPayDemographicsChange,
    purchase,
    onCancel,
    onSubmit,
}: SelectDemographicAndAddonPopoverContentProps) => {
    const calculatePartnerFee = (demographicsQuantity: number) => {
        if (demographicsQuantity === 0) return 0;

        let totalDemographicsQuantity = 0;

        for (const item of purchase.items) {
            totalDemographicsQuantity += getTotalDemographicsQuantity(item);
        }

        const partnerFee = getPartnerFee(purchase);
        return partnerFee ? (partnerFee.amount / totalDemographicsQuantity) * demographicsQuantity : 0;
    };

    const calculateTax = (item: PurchaseItem, demographics: SplitPayFieldValue[], addons: SplitPayFieldValue[]) => {
        let sum = 0;
        let demographicsQuantity = 0;
        for (const demographicItem of demographics) {
            sum += demographicItem.amount;
            demographicsQuantity += demographicItem.quantity;
        }

        for (const addonItem of addons) {
            const lineItemAddon = getAddons(item).find((addon) => addon.template.id === addonItem.id);
            if (lineItemAddon) {
                if (lineItemAddon.price) {
                    sum += lineItemAddon.price * addonItem.quantity;
                } else if (lineItemAddon.amount && lineItemAddon.quantity) {
                    sum += (lineItemAddon.amount / lineItemAddon.quantity) * addonItem.quantity;
                }
            }
        }

        const subtotalLineItems =
            getLineItemsByCode([LineItemCode.TAX, LineItemCode.FEE, LineItemCode.UNIT], item) ?? [];

        // IF subtotalLineItems is less then 1 - this is a migrated purchase, so it has different logic to calculate the taxes
        if (subtotalLineItems.length === 0) {
            const sumTaxByLineItems =
                getAmountByLineItemType("tax", item.lineItems) +
                getAmountByLineItemType("partner_fee", purchase.lineItems);
            return (sumTaxByLineItems / item.guests.length) * demographicsQuantity;
        }

        const discountedUnitSubtotal = getAmountByLineItemCode(LineItemCode.UNIT, item) + getTotalDiscountOnItem(item);

        // zero divided by zero in js equals to NaN, to avoid it we`ll just return 0
        const partnerFee = calculatePartnerFee(demographicsQuantity);
        const tax =
            sum === 0 && getAmountByLineItemCode(LineItemCode.UNIT, item) === 0
                ? 0
                : (sum / discountedUnitSubtotal) *
                  (getAmountByLineItemCode(LineItemCode.TAX, item) + getAmountByLineItemCode(LineItemCode.FEE, item));

        return partnerFee ? tax + partnerFee : tax;
    };

    const setQuantity = (item: PurchaseItem, newQuantity: number, id?: ID) => {
        const object = splitPayDemographics.items.find((object) => object.id === item.id);
        if (!object || !item) {
            return;
        }

        const newDemographics = object.splitPayItemValues.demographics.filter((value) => value.id !== id);
        const demographic = getPurchasableUnitLineItems(item).find((demographic) => demographic.template.id === id);
        const demographicPrice = demographic ? getDemographicPrice(demographic, item) : 0;

        newDemographics.push({
            id,
            quantity: newQuantity,
            name: demographic?.name ?? "",
            amount: demographicPrice * newQuantity,
        });
        const newValues = { ...splitPayDemographics };
        const newValuesObject = newValues.items.find((object) => object.id === item.id);

        if (newValuesObject) {
            newValuesObject.splitPayItemValues.demographics = newDemographics;
            const taxValue = calculateTax(item, newDemographics, newValuesObject.splitPayItemValues.addons);
            newValuesObject.splitPayItemValues.taxes = Number.isFinite(taxValue) ? taxValue : 0;
            onSplitPayDemographicsChange(newValues);
        }
    };

    const setAddonQuantity = (item: PurchaseItem, newQuantity: number, id?: ID) => {
        const object = splitPayDemographics.items.find((object) => object.id === item.id);
        if (!object || !item) {
            return;
        }

        const newAddons = object.splitPayItemValues.addons.filter((value) => value.id !== id);
        const lineItemAddon = getAddons(item).find((addon) => addon.template.id === id);
        let amount = 0;
        if (lineItemAddon) {
            if (lineItemAddon.price) {
                amount = lineItemAddon.price * newQuantity;
            } else if (lineItemAddon.amount) {
                amount = (lineItemAddon.amount / (lineItemAddon.quantity ?? 1)) * newQuantity;
            }
        }

        newAddons.push({
            id,
            quantity: newQuantity,
            name: lineItemAddon?.name ?? "",
            amount,
        });
        const newValues = { ...splitPayDemographics };
        const newValuesObject = newValues.items.find((object) => object.id === item.id);

        if (newValuesObject) {
            newValuesObject.splitPayItemValues.addons = newAddons;
            const addonTaxValue = calculateTax(item, newValuesObject.splitPayItemValues.demographics, newAddons);
            newValuesObject.splitPayItemValues.taxes = Number.isFinite(addonTaxValue) ? addonTaxValue : 0;
            onSplitPayDemographicsChange(newValues);
        }
    };

    const totalSum = getDemographicsTotalSum(splitPayDemographics, purchase);

    if (purchase.items.length > 1) {
        return (
            <div className="p-4">
                {purchase.items.map((item) => (
                    <MultiItemPopOverContent
                        key={item.id}
                        values={splitPayDemographics.items.find((object) => object.id === item.id)!}
                        setQuantity={setQuantity}
                        setAddonQuantity={setAddonQuantity}
                        selectedItem={item as ExperiencePurchaseItem}
                    />
                ))}
                <Breakdown.SubtotalItem info="Total" value={totalSum} />
                {totalSum > purchase.balance && <SplitPayWarningAlert purchase={purchase} totalSum={totalSum} />}
                <Footer
                    onSubmit={() => {
                        onSubmit(totalSum);
                    }}
                    onCancel={onCancel}
                />
            </div>
        );
    }

    return (
        <div className="p-4" data-testid="pay-for-guest-addon-popup">
            <SelectDemographicItemContent
                values={splitPayDemographics.items[0]}
                setQuantity={setQuantity}
                setAddonQuantity={setAddonQuantity}
                selectedItem={purchase.items[0] as ExperiencePurchaseItem}
            />
            <Breakdown currency={purchase.currency}>
                <Breakdown.SubtotalItem info="Total" value={totalSum} />
            </Breakdown>
            {totalSum > purchase.balance && <SplitPayWarningAlert purchase={purchase} totalSum={totalSum} />}
            <Footer onSubmit={() => onSubmit(totalSum)} onCancel={onCancel} />
        </div>
    );
};

const getPurchasableUnitLineItems = (item: PurchaseItem) =>
    item.lineItems.filter((lineItem) => {
        const type = isEquipmentItem(item) ? LineItemType.EQUIPMENT : LineItemType.DEMOGRAPHIC;
        return "type" in lineItem && lineItem.type === type && !!lineItem.quantity;
    });

const getAddons = (item: PurchaseItem) =>
    item.lineItems.filter((lineItem) => {
        return "type" in lineItem && lineItem.type === LineItemType.ADDON && !!lineItem.quantity;
    }) as AddonLineItem[];

const getAmountByLineItemCode = (code: LineItemCode, item: PurchaseItem) =>
    item.lineItems.find((lineItem) => lineItem.code === code)?.amount ?? 1;

const getLineItemByCode = (code: LineItemCode, item: PurchaseItem): LineItem | undefined => {
    return item.lineItems.find((lineItem) => lineItem.code === code);
};

const getLineItemsByCode = (codes: LineItemCode[], item: PurchaseItem) => {
    return item.lineItems.filter((lineItem) => {
        return codes.includes(lineItem.code);
    });
};

/** Returns sum of all the demographic/equipment quantities for PurchaseItem */
const getTotalDemographicsQuantity = (item: PurchaseItem) => {
    let totalQuantity = 0;

    for (const lineItem of getPurchasableUnitLineItems(item)) {
        if (lineItem.code === LineItemCode.PER_OUTING_PRICE) continue;
        totalQuantity += lineItem.quantity ?? 0;
    }

    return totalQuantity;
};

const getTotalDiscountOnItem = (item: PurchaseItem) => {
    return sumBy(
        getItemLineItemsByTypes(item, [
            LineItemType.DISCOUNT,
            LineItemType.AFFILIATE_DISCOUNT,
            LineItemType.COUPON_DISCOUNT,
            LineItemType.PACKAGE_DISCOUNT,
            LineItemType.PARTNER_PACKAGE_DISCOUNT,
        ]),
        "amount",
    );
};

/**
 * Returns the price for given demographic.<br/>
 * If the PurchaseItem.lineItems contain a demographic with code *per_outing_price*, returns fixed_amount / total_demographics_quantity
 *
 * @param demographic Demographic for which we want to get the price
 * @param item PurchaseItem : in which given demographic belongs to
 */
const getDemographicPrice = (demographic: DemographicLineItem | LineItem, item: PurchaseItem): number => {
    const perOutingDemographic = getLineItemByCode(LineItemCode.PER_OUTING_PRICE, item);
    const totalQuantity = getTotalDemographicsQuantity(item);

    if (perOutingDemographic?.amount) {
        return totalQuantity ? (perOutingDemographic.amount + getTotalDiscountOnItem(item)) / totalQuantity : 0;
    }

    return demographic.price ? demographic.price + getTotalDiscountOnItem(item) / totalQuantity : 0;
};

const getAmountByLineItemType = (type: string, lineItems: LineItem[]) =>
    lineItems.find((lineItem) => lineItem.type === type)?.amount ?? 1;

const MultiItemPopOverContent = ({
    values,
    selectedItem,
    setQuantity,
    setAddonQuantity,
}: SelectDemographicItemContentProps) => {
    const { data: experience, isLoading } = useResource<Experience>(["products", selectedItem.product.id]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <>
            <ExperienceTile item={selectedItem} product={experience!} />
            <SelectDemographicItemContent
                values={values}
                setQuantity={setQuantity}
                setAddonQuantity={setAddonQuantity}
                selectedItem={selectedItem}
            />
        </>
    );
};

interface SelectDemographicItemContentProps {
    values: { id: string; splitPayItemValues: SplitPaymentInitialValues };
    selectedItem: ExperiencePurchaseItem;
    setQuantity: (item: PurchaseItem, newQuantity: number, id?: ID) => void;
    setAddonQuantity: (item: PurchaseItem, newQuantity: number, id?: ID) => void;
}

const SelectDemographicItemContent = ({
    values,
    selectedItem,
    setQuantity,
    setAddonQuantity,
}: SelectDemographicItemContentProps) => {
    const seller = useSeller();

    const demographics = getPurchasableUnitLineItems(selectedItem);

    const addons = selectedItem.lineItems.filter((lineItem) => {
        return "type" in lineItem && lineItem.type === LineItemType.ADDON;
    }) as AddonLineItem[];

    const { splitPayItemValues } = values;

    return (
        <div className="divide-y divide-gray-lighter">
            <div className="pb-3">
                <Label>{isEquipmentItem(selectedItem) ? "Equipment" : "Demographics"}</Label>

                {demographics.map((demographic) => (
                    <SplitPayDemographicsLineItem
                        key={demographic.template.id}
                        splitPayFieldValue={splitPayItemValues.demographics.find(
                            (value) => value.id === demographic.template.id,
                        )}
                        max={demographic.quantity ?? 0}
                        name={demographic.name}
                        onChange={(newQuantity: number) => {
                            setQuantity(selectedItem, newQuantity, demographic.template.id);
                        }}
                    />
                ))}

                {addons.length > 0 && <Label className="pt-4">Add-ons</Label>}

                {addons.map((addon) => (
                    <SplitPayDemographicsLineItem
                        key={addon.template.id}
                        splitPayFieldValue={splitPayItemValues.addons.find((value) => value.id === addon.template.id)}
                        max={addon.quantity}
                        name={addon.name}
                        onChange={(newQuantity: number) => {
                            setAddonQuantity(selectedItem, newQuantity, addon.template.id);
                        }}
                    />
                ))}
            </div>

            {!!splitPayItemValues.taxes && (
                <div
                    className="flex w-full items-center justify-between space-x-4 pt-2 pb-2"
                    data-testid="pay-for-guestAndAddon-lineItem"
                >
                    <div>Taxes & Fees</div>
                    <Currency currency={seller.currency}>{numberFormat(splitPayItemValues.taxes)}</Currency>
                </div>
            )}
        </div>
    );
};

interface SplitPayDemographicsLineItemProps {
    splitPayFieldValue?: SplitPayFieldValue;
    max: number;
    onChange: (quantity: number) => void;
    name?: string;
}

const SplitPayDemographicsLineItem = ({
    splitPayFieldValue,
    max,
    name,
    onChange,
}: SplitPayDemographicsLineItemProps) => {
    const seller = useSeller();
    return (
        <div
            className="flex w-full items-center justify-between space-x-4 pt-2"
            data-testid="pay-for-guestAndAddon-lineItem"
        >
            <div className="flex space-x-4">
                <QuantitySpinner
                    value={splitPayFieldValue?.quantity ?? 0}
                    min={0}
                    max={max}
                    size="medium"
                    classNames={{ label: "font-bold" }}
                    shouldSyncValue={false}
                    onChange={onChange}
                />
                <div className="font-bold">{name}</div>
            </div>
            <Currency currency={seller.currency}>{splitPayFieldValue?.amount ?? 0}</Currency>
        </div>
    );
};

interface FooterProps {
    onCancel: () => void;
    onSubmit: (amount: number, values: SplitPayDemographics) => void;
}

const Footer = ({ onSubmit, onCancel }: FooterProps) => {
    return (
        <div className="flex w-full flex-row-reverse space-x-2 space-x-reverse pt-3">
            <Button type="button" color="primary" onClick={onSubmit}>
                Apply
            </Button>
            <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
        </div>
    );
};
